/**
 * Superadmin control-plane audit log (ADR-0036 Fase 2 — F2.4).
 *
 * Reads `GET /api/platform/audit` — every superadmin action (tenant lifecycle) recorded
 * on `public.audit_log` with `actor_type='platform_admin'`, enriched with the actor email
 * + target tenant slug. The old "solo impersonation" toggle is gone with the feature
 * (ADR-0081 S0); HISTORICAL `impersonation_*` rows still appear in the unfiltered list,
 * which is the point of an audit log.
 */
import { useQuery } from "@tanstack/react-query";
import { Loader2, ScrollText, TriangleAlert } from "lucide-react";
import * as React from "react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { EmptyState, PageContainer, PageContent, PageHeader } from "@/components/ui/page";
import { Table, Tbody, Td, Th, Thead, Tr } from "@/components/ui/table";
import {
  type PlatformAuditEntry,
  PLATFORM_EVENT_TYPES,
  PlatformApiError,
  platformApi,
} from "@/lib/platform";

const PAGE_SIZE = 25;

/** Friendly IT labels for the control-plane event vocabulary. */
const EVENT_LABEL: Record<string, string> = {
  tenant_provisioned: "Tenant creato",
  tenant_suspended: "Tenant sospeso",
  tenant_active: "Tenant riattivato",
  tenant_deprovisioned: "Tenant eliminato",
  // Retired (ADR-0081 S0) but kept as LABELS: historical rows still carry these types
  // and an audit log that renders raw event codes for old rows is a worse log.
  impersonation_started: "Impersonation avviata",
  impersonation_elevated: "Scrittura abilitata",
};

function eventLabel(t: string): string {
  return EVENT_LABEL[t] ?? t;
}

function isImpersonation(t: string): boolean {
  return t.startsWith("impersonation_");
}

export function PlatformAuditPage() {
  const [eventType, setEventType] = React.useState<string>("");
  const [page, setPage] = React.useState(1);

  // Reset to page 1 whenever a filter changes.
  React.useEffect(() => {
    setPage(1);
  }, [eventType]);

  const logQ = useQuery({
    queryKey: ["platform", "audit", { eventType, page }],
    queryFn: () =>
      platformApi.listAudit({
        event_type: eventType || undefined,
        page,
        page_size: PAGE_SIZE,
      }),
    placeholderData: (prev) => prev,
  });

  const rows = logQ.data?.items ?? [];
  const total = logQ.data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <PageContainer>
      <PageHeader
        title="Audit del control plane"
        description="Azioni dei superadmin sul control plane: ciclo di vita dei tenant."
      />
      <PageContent className="space-y-4">
        <div className="flex flex-wrap items-center gap-2">
          <select
            value={eventType}
            onChange={(e) => setEventType(e.target.value)}
            className="h-9 rounded-md border border-border/60 bg-surface px-2 text-sm disabled:opacity-50"
            aria-label="Filtra per evento"
          >
            <option value="">Tutti gli eventi</option>
            {PLATFORM_EVENT_TYPES.map((t) => (
              <option key={t} value={t}>
                {eventLabel(t)}
              </option>
            ))}
          </select>
          <span className="ml-auto text-xs text-muted-foreground tabular-nums">
            {total} event{total === 1 ? "o" : "i"}
          </span>
        </div>

        {logQ.isError ? (
          <EmptyState
            icon={TriangleAlert}
            title="Impossibile caricare l'audit"
            description={
              logQ.error instanceof PlatformApiError ? logQ.error.message : "Errore di rete."
            }
            action={
              <Button variant="ghost" onClick={() => logQ.refetch()}>
                Riprova
              </Button>
            }
          />
        ) : rows.length === 0 && !logQ.isLoading ? (
          <EmptyState
            icon={ScrollText}
            title="Nessun evento"
            description="Le azioni del control plane appariranno qui."
          />
        ) : (
          <>
            <div className="overflow-hidden rounded-lg border border-border/60">
              <Table>
                <Thead>
                  <Tr>
                    <Th>Quando</Th>
                    <Th>Evento</Th>
                    <Th>Superadmin</Th>
                    <Th>Tenant</Th>
                    <Th>Dettagli</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {rows.map((row) => (
                    <AuditRow key={row.audit_id} row={row} />
                  ))}
                </Tbody>
              </Table>
            </div>

            <div className="flex items-center justify-between text-sm">
              <span className="text-muted-foreground tabular-nums">
                Pagina {page} di {totalPages}
              </span>
              <div className="flex items-center gap-2">
                <Button
                  size="sm"
                  variant="ghost"
                  disabled={page <= 1 || logQ.isFetching}
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                >
                  Prec.
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  disabled={page >= totalPages || logQ.isFetching}
                  onClick={() => setPage((p) => p + 1)}
                >
                  Succ.
                </Button>
                {logQ.isFetching && (
                  <Loader2
                    className="h-4 w-4 animate-spin text-muted-foreground"
                    strokeWidth={1.5}
                  />
                )}
              </div>
            </div>
          </>
        )}
      </PageContent>
    </PageContainer>
  );
}

function AuditRow({ row }: { row: PlatformAuditEntry }) {
  const imp = isImpersonation(row.event_type);
  const userId = typeof row.details?.user_id === "string" ? (row.details.user_id as string) : null;
  const readonly = row.details?.readonly;
  return (
    <Tr>
      <Td className="whitespace-nowrap text-xs text-muted-foreground">
        {new Date(row.occurred_at).toLocaleString()}
      </Td>
      <Td>
        <Badge tone={imp ? "warning" : "neutral"}>{eventLabel(row.event_type)}</Badge>
      </Td>
      <Td className="text-sm">{row.actor_email ?? row.actor_id ?? "—"}</Td>
      <Td className="text-sm">
        {row.target_slug ? (
          <span className="font-mono text-xs">{row.target_slug}</span>
        ) : (
          <span className="text-muted-foreground">—</span>
        )}
      </Td>
      <Td className="text-xs text-muted-foreground">
        {imp ? (
          <span>
            utente <span className="font-mono">{userId ? userId.slice(0, 8) : "?"}</span>
            {readonly === true ? " · sola lettura" : readonly === false ? " · scrittura" : ""}
          </span>
        ) : (
          row.action
        )}
      </Td>
    </Tr>
  );
}
